import type {
  StringableCSSProperties,
  StyleElements,
} from "replo-runtime/shared/utils/styles";
import type { CustomPropsRecord } from "schemas/component";
import type { AlchemyActionType } from "schemas/runtimeActions";
import type { ComponentConfig } from "../../components";

import { convertToLegacyProps } from "../../../shared/utils/props";
import fullScreenIconConfig from "../FullScreenIcon/config";
import muteIconConfig from "../MuteIcon/config";
import playIconConfig from "../PlayIcon/config";

function getConfigurableProps(): CustomPropsRecord {
  return {
    _autoplay: {
      name: "Autoplay",
      type: "boolean",
      description: "If enabled, the video will autoplay on mute when loaded",
      defaultValue: false,
    },
    _loop: {
      name: "Autoloop",
      type: "boolean",
      defaultValue: false,
      description: "If enabled, the video will loop after it ends",
    },
    _preload: {
      name: "Preload",
      type: "boolean",
      defaultValue: false,
      description:
        "If enabled, the browser will give preference to loading this video content. Use this when it's likely that visitors will view the video.",
    },
    _defaultMuted: {
      name: "Default Muted",
      type: "boolean",
      defaultValue: true,
      description: "If enabled, the video will play on mute by default",
    },
    _autoFullscreen: {
      name: "Auto Full Screen",
      type: "boolean",
      defaultValue: false,
      description:
        "If enabled, playing the video will cause it to become full screen.",
    },
    _placeholderImage: {
      name: "Placeholder Image",
      type: "image",
      defaultValue: null,
      description:
        "If set, this image will show before the video starts playing",
    },
    _placeholderPlayIcon: {
      name: "Placeholder Play Icon",
      type: "image",
      defaultValue: null,
      description: "Element to use as the play icon in preview",
      allowsSettingObjectFit: false,
    },
  };
}

export const customProperties = {
  objectFit: "--rof",
  objectPosition: "--rop",
};

function hasStyleOverride(
  propValue: unknown,
): propValue is { style?: StringableCSSProperties | null } {
  if (!propValue || typeof propValue !== "object" || !("style" in propValue)) {
    return false;
  }

  const style = propValue.style;
  return style === undefined || style === null || typeof style === "object";
}

function getStyleOverride(propValue: unknown): StringableCSSProperties {
  if (!hasStyleOverride(propValue) || !propValue.style) {
    return {};
  }

  return propValue.style;
}

export const styleElements: StyleElements = {
  root: {
    defaultStyles: {},
  },
  inner: {
    defaultStyles: {
      // Note (Noah, 2024-05-11, USE-954): In certain contexts, Safari calculates
      // 100% as 100% of the containing flex container (which may not be the parent
      // element). This is here so that the height: 100% which we have on inner
      // wrapper is relative to this component, not this component's parent Container
      display: "flex",
      position: "relative",
      width: "100%",
      height: "100%",
      objectFit: "inherit",
      objectPosition: "inherit",
    },
  },
  player: {
    defaultStyles: {
      width: "100%",
      height: "100%",
      objectFit: `var(${customProperties.objectFit}, inherit)`,
      objectPosition: `var(${customProperties.objectPosition}, inherit)`,
    },
  },
  placeholderImage: {
    defaultStyles: {
      position: "absolute",
      inset: 0,
      // Non-inset necessary for safari
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      height: "100%",
      cursor: "pointer",
    },
    overrideStyles({ component }) {
      const placeholderImage = component.props._placeholderImage;
      return { ...getStyleOverride(placeholderImage) };
    },
  },
  placeholderPlayIcon: {
    defaultStyles: {
      position: "absolute",
      inset: 0,
      // Non-inset necessary for safari
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    },
    overrideStyles({ component }) {
      const placeholderPlayIcon = component.props._placeholderPlayIcon;
      return { ...getStyleOverride(placeholderPlayIcon) };
    },
  },
};

const actionTypes = [
  "togglePlay",
  "toggleMute",
  "toggleFullScreen",
] as const satisfies AlchemyActionType[];

export type ActionType = (typeof actionTypes)[number];

const config = {
  renderData: {
    customProps: convertToLegacyProps(getConfigurableProps()),
    newInstancesUseNumbering: true,
    acceptsArbitraryChildren: () => false,
    isAlwaysDynamic: false,
    canvasIndicatorDragDirections: [],
    allowsLayoutModification: false,
    canContainChildren: false,
    styleElements,
    actionTypes,
  },
  children: {
    playIcon: playIconConfig,
    muteIcon: muteIconConfig,
    fullScreenIcon: fullScreenIconConfig,
  },
} satisfies ComponentConfig;

export default config;
